<template>
  <transition name="modal">
    <div v-if="show" class="modal-mask" @click.self="closeModal">
      <div class="modal-wrapper">
        <div class="modal-container">
          <!-- Header -->
          <div class="modal-header">
            <h3 class="text-xl font-bold text-gray-800">
              {{ $t("message.request_restock_modal.report_lost_sale") }}
            </h3>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <!-- Product Info -->
            <div class="bg-gray-50 p-4 rounded-lg mb-4">
              <div class="flex items-start gap-4">
                <img
                  v-if="productImage"
                  :src="productImage"
                  :alt="productCode"
                  class="w-20 h-20 object-cover rounded"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="w-20 h-20 bg-gray-200 rounded flex items-center justify-center"
                >
                  <span class="text-gray-400 text-xs">No image</span>
                </div>
                <div class="flex-1">
                  <div class="font-semibold text-gray-800 text-lg">
                    {{ productCode }}
                  </div>
                  <div class="text-sm text-gray-600 mt-1">
                    {{ productDescription || "No description available" }}
                  </div>
                  <div class="text-sm text-gray-500 mt-2">
                    Current Stock:
                    <span
                      class="font-medium"
                      :class="
                        currentStock > 0 ? 'text-green-600' : 'text-red-600'
                      "
                    >
                      {{ currentStock }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading State for Check -->
            <div
              v-if="checkingExisting"
              class="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-4 flex items-center gap-3"
            >
              <svg
                class="animate-spin h-5 w-5 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Checking if product is already requested...</span>
            </div>

            <!-- Already Requested Info -->
            <div
              v-if="existingRequest && !checkingExisting"
              class="bg-blue-50 border border-blue-300 text-blue-800 px-4 py-3 rounded mb-4"
            >
              <div class="flex items-start gap-2">
                <svg
                  class="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div class="flex-1">
                  <p class="font-semibold mb-1">
                    {{
                      $t(
                        "message.request_restock_modal.product_already_requested"
                      )
                    }}
                  </p>
                  <p class="text-sm">
                    {{
                      $t(
                        "message.request_restock_modal.product_requested_info",
                        {
                          quantity: existingRequest.requestedQuantity,
                          user: existingRequest.userName,
                          date: formatDate(existingRequest.createdAt),
                          reason: $t(
                            `message.pending_products.reasons.${existingRequest.reason}`
                          )
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Quantity Input -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.request_restock_modal.requested_quantity") }} *
              </label>
              <input
                v-model.number="requestedQuantity"
                type="number"
                min="1"
                :placeholder="
                  $t('message.request_restock_modal.enter_quantity')
                "
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                @keyup.enter="submitRequest"
                :disabled="checkingExisting"
              />
            </div>

            <!-- Reason Dropdown -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.pending_products.reason") }} *
              </label>
              <select
                v-model="reason"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                :disabled="checkingExisting"
              >
                <option value="" disabled>
                  {{ $t("message.pending_products.select_reason") }}
                </option>
                <option
                  v-for="option in reasonOptions"
                  :key="option"
                  :value="option"
                >
                  {{ $t(`message.pending_products.reasons.${option}`) }}
                </option>
              </select>
            </div>

            <!-- Comment Input -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t("message.request_restock_modal.comment") }}
              </label>
              <textarea
                v-model="comment"
                :placeholder="$t('message.request_restock_modal.enter_comment')"
                rows="3"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-none"
                :disabled="checkingExisting"
              />
            </div>

            <!-- Error Message -->
            <div
              v-if="error"
              class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
            >
              {{ error }}
            </div>

            <!-- Success Message -->
            <div
              v-if="success"
              class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4"
            >
              {{ success }}
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer">
            <button
              @click="closeModal"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              :disabled="loading"
            >
              {{ $t("message.request_restock_modal.cancel") }}
            </button>
            <button
              @click="submitRequest"
              :disabled="
                loading ||
                  !requestedQuantity ||
                  requestedQuantity < 1 ||
                  !reason ||
                  checkingExisting
              "
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <span v-if="loading">
                <svg
                  class="animate-spin h-5 w-5 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {{ $t("message.request_restock_modal.requesting") }}
              </span>
              <span v-else>
                {{ $t("message.request_restock_modal.report_lost_sale") }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  createPendingProduct,
  checkPendingProduct
} from "@/api/store/PendingProductsService";

export default {
  name: "ReportLostSaleModal",
  props: {
    show: {
      type: Boolean,
      required: true
    },
    productCode: {
      type: String,
      required: true
    },
    productDescription: {
      type: String,
      default: ""
    },
    productImage: {
      type: String,
      default: ""
    },
    currentStock: {
      type: Number,
      default: 0
    },
    departmentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      requestedQuantity: 1,
      reason: "",
      comment: "",
      loading: false,
      checkingExisting: false,
      error: null,
      success: null,
      existingRequest: null,
      reasonOptions: ["out_of_stock", "price", "other"]
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset form when modal opens
        this.requestedQuantity = 1;
        this.reason = "";
        this.comment = "";
        this.error = null;
        this.success = null;
        this.existingRequest = null;

        // Check if product is already requested
        this.checkIfAlreadyRequested();
      }
    }
  },
  methods: {
    closeModal() {
      if (!this.loading) {
        this.$emit("close");
      }
    },
    async submitRequest() {
      if (
        this.loading ||
        !this.requestedQuantity ||
        this.requestedQuantity < 1
      ) {
        return;
      }

      if (!this.reason) {
        this.error = "Reason is required";
        return;
      }

      this.loading = true;
      this.error = null;
      this.success = null;

      try {
        // Always create a new request
        const productData = {
          nkr_code: this.productCode,
          departamento_id: this.departmentId,
          requested_quantity: this.requestedQuantity,
          reason: this.reason,
          comment: this.comment
        };

        const response = await createPendingProduct(productData);

        if (response.status) {
          this.success =
            response.message ||
            this.$t(
              "message.request_restock_modal.request_submitted_successfully"
            );
          // Emit success event
          this.$emit("success", response.data);

          // Close modal after 1.5 seconds
          setTimeout(() => {
            this.closeModal();
          }, 1500);
        } else {
          this.error =
            response.message ||
            this.$t("message.request_restock_modal.failed_to_submit_request");
        }
      } catch (err) {
        this.error =
          err.response?.data?.message ||
          err.message ||
          "An error occurred while submitting the request";
        console.error("Error submitting restock request:", err);
      } finally {
        this.loading = false;
      }
    },
    async checkIfAlreadyRequested() {
      this.checkingExisting = true;
      this.existingRequest = null;
      this.error = null;

      try {
        const params = {
          nkr_code: this.productCode,
          departamento_id: this.departmentId,
          status: "reported" // Only check for 'reported' status
        };

        const response = await checkPendingProduct(params);

        if (response.status && response.data) {
          // Product exists in pending products - just show info
          this.existingRequest = response.data;
        }
      } catch (err) {
        // If error occurs, just log it but don't block the user
        console.error("Error checking existing request:", err);
        // Don't show error to user, just proceed normally
      } finally {
        this.checkingExisting = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    handleImageError(event) {
      event.target.style.display = "none";
    }
  }
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
}

.modal-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 150px);
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}
</style>
